import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, AttachFile as AttachFileIcon, Edit as EditIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import DocumentViewerModal from '../DocumentViewerModal';
import { STOCK_ISSUE_LABELS } from './VaccinationCard';
import { vaccineStandings, VaccineStatus } from '../../pages/ClinicalRecords/chartDates';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { formatAnchoredDayOrInstant, formatCalendarDate } from '../../utils/dateUtils';
import { isCogginsName } from '../../utils/coggins';

type CardStatus = VaccineStatus | 'NO_HISTORY';
type RowStatus = VaccineStatus | 'SUPERSEDED';

const LABEL: Record<CardStatus | RowStatus, string> = {
  OVERDUE: 'Overdue',
  DUE_SOON: 'Due soon',
  CURRENT: 'Current',
  NO_BOOSTER: 'No booster',
  NO_HISTORY: 'Never given',
  SUPERSEDED: 'Replaced by a newer dose',
};
const CHIP_COLOR: Record<CardStatus, 'error' | 'warning' | 'success' | 'default'> = {
  OVERDUE: 'error',
  DUE_SOON: 'warning',
  CURRENT: 'success',
  NO_BOOSTER: 'default',
  NO_HISTORY: 'default',
};
const DOT_COLOR: Record<RowStatus, string> = {
  OVERDUE: 'error.main',
  DUE_SOON: 'warning.main',
  CURRENT: 'success.main',
  NO_BOOSTER: 'text.disabled',
  SUPERSEDED: 'text.disabled',
};
const FILTERS: Array<CardStatus | 'ALL'> = ['ALL', 'OVERDUE', 'DUE_SOON', 'CURRENT', 'NO_BOOSTER', 'NO_HISTORY'];
const DAY = 'MMM d, yyyy';

interface PatientVaccinationsTabProps {
  /** The rows the table lists: the page's search, date range and archived toggle already applied. */
  vaccinations: any[];
  /** Every vaccination of the horse. Status always reads the whole history; a search narrows only the table. */
  allVaccinations: any[];
  /** The clinic's own vaccine list, so a vaccine this horse never had still gets a card. */
  catalogNames: string[];
  attachments?: Record<string, any>;
  onAdd?: () => void;
  onEdit?: (vaccination: any) => void;
  onMenu?: (event: React.MouseEvent<HTMLElement>, id: string) => void;
}

/**
 * The patient's Vaccinations tab (spec §15, §16, ported from the practice manager's design): a card per
 * vaccine with its standing from the newest dose, then the history, where only that newest dose is coloured
 * and older ones read as replaced. Due is a calendar day from the clinic's today (`chartDates`).
 */
const PatientVaccinationsTab: React.FC<PatientVaccinationsTabProps> = ({
  vaccinations,
  allVaccinations,
  catalogNames,
  attachments = {},
  onAdd,
  onEdit,
  onMenu,
}) => {
  const tz = useClinicTimezone();
  const [filter, setFilter] = useState<CardStatus | 'ALL'>('ALL');
  const [viewing, setViewing] = useState<{ attachment: any; title: string } | null>(null);

  const standings = useMemo(() => vaccineStandings(allVaccinations, tz), [allVaccinations, tz]);
  const cards = useMemo(() => {
    const given = new Set(standings.map((s) => s.name.toLowerCase()));
    const never = Array.from(new Set(catalogNames.map((n) => n.trim())))
      .filter((n) => n && !isCogginsName(n) && !given.has(n.toLowerCase()))
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name, status: 'NO_HISTORY' as const, nextDueDate: null, lastGiven: null }));
    return [...standings, ...never];
  }, [standings, catalogNames]);
  const rowStatus = useMemo(() => {
    const newest = new Map(standings.map((s) => [s.newestId, s.status]));
    return (v: any): RowStatus | null =>
      newest.get(v.id) ?? (v.isArchived || !v.vaccineName || isCogginsName(v.vaccineName) ? null : 'SUPERSEDED');
  }, [standings]);

  // A search narrows the cards to the vaccines it lists; their status still comes from everything.
  const listed = new Set(vaccinations.map((v) => (v.vaccineName ?? '').trim().toLowerCase()));
  const narrowed = vaccinations.length < allVaccinations.filter((v) => !v.isArchived).length;
  const inView = narrowed ? cards.filter((c) => listed.has(c.name.toLowerCase())) : cards;
  const shown = filter === 'ALL' ? inView : inView.filter((c) => c.status === filter);
  const sorted = [...vaccinations].sort(
    (a, b) => (Date.parse(b.dateAdministered ?? '') || 0) - (Date.parse(a.dateAdministered ?? '') || 0),
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5} flexWrap="wrap">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {FILTERS.map((f) => (
            <Chip
              key={f}
              size="small"
              label={f === 'ALL' ? 'All' : LABEL[f]}
              color={filter === f ? 'primary' : 'default'}
              variant={filter === f ? 'filled' : 'outlined'}
              onClick={() => setFilter(f)}
            />
          ))}
        </Stack>
        {onAdd && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd}>
            Add Vaccination
          </Button>
        )}
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {shown.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.name}>
            <Paper variant="outlined" sx={{ p: 1.75, height: '100%', borderStyle: c.status === 'NO_HISTORY' ? 'dashed' : 'solid' }}>
              <Chip
                size="small"
                label={LABEL[c.status]}
                color={CHIP_COLOR[c.status]}
                variant={c.status === 'NO_HISTORY' ? 'outlined' : 'filled'}
                sx={{ mb: 1 }}
              />
              <Typography variant="subtitle2" fontWeight={600}>
                {c.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.nextDueDate
                  ? `Due ${formatCalendarDate(c.nextDueDate, DAY)}`
                  : c.lastGiven
                    ? `Last given ${formatAnchoredDayOrInstant(c.lastGiven, tz, DAY)}`
                    : 'Never recorded'}
              </Typography>
            </Paper>
          </Grid>
        ))}
        {shown.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No vaccines in this state.
            </Typography>
          </Grid>
        )}
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Given</TableCell>
              <TableCell>Vaccine</TableCell>
              <TableCell>Lot</TableCell>
              <TableCell>Given by</TableCell>
              <TableCell>Next booster</TableCell>
              {(onEdit || onMenu) && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No vaccinations recorded
                </TableCell>
              </TableRow>
            )}
            {sorted.map((v) => {
              const status = rowStatus(v);
              const attachment = attachments[v.id];
              // A migrated document's notes are "[Imported Document] Title\nSource file: x": show the title.
              const imported = typeof v.notes === 'string' && v.notes.startsWith('[Imported Document]');
              const notes = imported ? v.notes.split('\n')[0].replace('[Imported Document] ', '').trim() : v.notes;
              return (
                <TableRow key={v.id} sx={{ opacity: v.isArchived ? 0.55 : 1 }}>
                  {/* MIXED per row (DATE-KINDS §1.2): a barn day reads its date part, a saved moment the clinic's day. */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAnchoredDayOrInstant(v.dateAdministered, tz, DAY) || '-'}</TableCell>
                  <TableCell>
                    {v.vaccineName}
                    {v.manufacturer && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {v.manufacturer}
                      </Typography>
                    )}
                    {imported && <Chip label="Imported" size="small" variant="outlined" sx={{ ml: 1 }} />}
                    {notes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {notes}
                      </Typography>
                    )}
                    {attachment?.storageUrl && (
                      <Button
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => setViewing({ attachment, title: v.vaccineName })}
                        sx={{ mt: 0.5, px: 0 }}
                      >
                        View document
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {v.lotNumber ? (
                      <>
                        {v.lotNumber}
                        {v.lotExpiresOn && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            exp {formatCalendarDate(v.lotExpiresOn, DAY)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      '-'
                    )}
                    {v.doseMl ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {v.doseMl} mL
                      </Typography>
                    ) : null}
                    {(v.stockIssues ?? []).map((issue: string) => (
                      <Chip
                        key={issue}
                        size="small"
                        variant="outlined"
                        color={issue === 'EXPIRED_LOT' ? 'error' : 'warning'}
                        label={STOCK_ISSUE_LABELS[issue] ?? issue}
                        sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>{v.administeredBy || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {status && (
                        <Box component="span" aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DOT_COLOR[status], flexShrink: 0 }} />
                      )}
                      {/* CALENDAR `Vaccination.nextDueDate`: the date part, never re-zoned. */}
                      <span>{v.nextDueDate ? formatCalendarDate(v.nextDueDate, DAY) : '-'}</span>
                    </Box>
                    {status && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ ml: 2.25, color: status === 'SUPERSEDED' || status === 'NO_BOOSTER' ? 'text.secondary' : DOT_COLOR[status] }}
                      >
                        {LABEL[status]}
                      </Typography>
                    )}
                  </TableCell>
                  {(onEdit || onMenu) && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {onEdit && (
                        <IconButton size="small" aria-label={`Edit ${v.vaccineName}`} title="Edit" onClick={() => onEdit(v)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {onMenu && (
                        <IconButton size="small" aria-label={`More options for ${v.vaccineName}`} title="More options" onClick={(e) => onMenu(e, v.id)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {viewing && (
        <DocumentViewerModal
          open
          onClose={() => setViewing(null)}
          url={`/api${viewing.attachment.storageUrl}`}
          title={viewing.title}
          mimeType={viewing.attachment.mimeType}
        />
      )}
    </Box>
  );
};

export default PatientVaccinationsTab;
